import type { DocumentBlock, DocumentContent } from '@morph/spec';
import { validateDocumentContent, CURRENT_DOCUMENT_VERSION } from '@morph/spec';
import type { EditorAuthority } from './authority.js';
import type { EdgeId, ManifestRef, ObjectId, PropertyKeyId } from './ids.js';
import type { StoredObject } from './model.js';
import type { ObjectReader } from './read.js';
import type { ObjectStore } from './store.js';
import type { Value } from './value.js';
import type { CommitResult, EditSession } from './write.js';

/** Private storage identities for the canonical document graph. */
export interface DocumentKeys {
  readonly kind: PropertyKeyId;
  readonly text: PropertyKeyId;
  readonly level: PropertyKeyId;
  readonly order: PropertyKeyId;
  readonly roots: PropertyKeyId;
  readonly children: PropertyKeyId;
  readonly target: PropertyKeyId;
  /** The migration envelope, parked on the document object. See `persistDocument`. */
  readonly legacy: PropertyKeyId;
  /**
   * THE FORMAT VERSION, and it rides on the document object — decision `0075`,
   * task `0200`. Before this it rode on nothing: the envelope was reconstructed
   * with a hard-coded 1 on every load, so the contract a reader must satisfy
   * was not a fact about the stored document at all.
   *
   * ABSENT MEANS 1, which is not a default chosen for convenience: every
   * document this product has persisted so far was written by a build whose
   * only version was 1, so absence IS the v1 claim. The spec's grandfathering
   * rule then reads such a document even when it carries a v2 kind, and a
   * genuine edit stamps the current version on the way back out.
   */
  readonly version: PropertyKeyId;
  /**
   * The edge that attaches this block to its structural parent, and the edge to
   * its embed target — recorded so a later save can take them back out again.
   *
   * `session.link` mints and returns an `EdgeId`; `session.unlink` consumes
   * one; and **no read API hands one back**. `TraversalEdge` deliberately
   * carries an opaque `RelationTargetHandle` instead, because decision 0016
   * refuses to let anything above the store name an edge by its address.
   *
   * So the document vocabulary keeps its own record. A block knows the edge
   * that holds it, which is exactly the edge that has to go when the block
   * moves, and a re-save can be precise instead of additive. A block has one
   * structural parent — the validator enforces it — so root and containment
   * edges share the one key.
   */
  readonly parentEdge: PropertyKeyId;
  readonly targetEdge: PropertyKeyId;
}

export interface DocumentManifests {
  readonly values: ManifestRef;
  readonly roots: ManifestRef;
  readonly children: ManifestRef;
  readonly targets: ManifestRef;
}

/**
 * Define the private keys once; callers persist the returned identities.
 *
 * **Containment is `relation.child`, embeds are `relation.peer`**, and the
 * difference is public meaning rather than bookkeeping. @Codex's P1 on its own
 * first version: separate manifests stopped the loader merging the sets, but
 * *"document roots and list members are advertised as peers, making canonical
 * containment semantically indistinguishable from citations or embed targets."*
 * Decision 0025 defines a document as an ordered TREE; a peer edge says the
 * opposite to every lens and traversal that reads it.
 *
 * Roots and children stay separate `PropertyKeyId`s under that one role, so the
 * loader still tells a document's roots from a list's items without asking what
 * the edge means.
 *
 * And the value keys stay PRIVATE. A competing draft mapped them onto public
 * roles — `status` for kind, `duration` for a serialization ordinal — which
 * would make every block genuinely carry a status and a duration to anything
 * holding the manifest. They are read by `PropertyKeyId` here and never enter
 * the public vocabulary.
 */
/**
 * WHAT EACH DOCUMENT KEY IS, in one table — @Codex's P1 on `d881f5b`.
 *
 * It was eleven `session.defineProperty` calls in an object literal, which is
 * the same thing said once and readable only in order. A TABLE is what lets
 * `completeDocumentKeys` below define a SUBSET, and that is the whole of the
 * migration: a returning installation is missing whichever keys were added
 * since it last booted, and the answer must not be "call the whole function
 * again" — `defineProperty` appends to the registry, so that mints eleven new
 * ids and orphans every document already written under the old ones.
 */
const DOCUMENT_KEY_DEFS = {
  kind: { label: 'Document block kind', kind: 'enum' },
  text: { label: 'Document block text', kind: 'text' },
  level: { label: 'Document heading level', kind: 'number' },
  order: { label: 'Document block order', kind: 'number' },
  legacy: { label: 'Document legacy envelope', kind: 'text' },
  version: { label: 'Document format version', kind: 'number' },
  parentEdge: { label: 'Document containment edge', kind: 'text' },
  targetEdge: { label: 'Document embed edge', kind: 'text' },
  roots: { label: 'Document roots', kind: 'ref', roleHint: 'relation.child' },
  children: { label: 'Document children', kind: 'ref', roleHint: 'relation.child' },
  target: { label: 'Document embed target', kind: 'ref', roleHint: 'relation.peer' },
} as const satisfies Record<keyof DocumentKeys, Parameters<EditSession['defineProperty']>[0]>;

/** Every document key's name, in the table's order. Exported so a host can
 *  ask whether a record it persisted is still complete without opening an
 *  edit session to find out. */
export const DOCUMENT_KEY_NAMES =
  Object.keys(DOCUMENT_KEY_DEFS) as readonly (keyof DocumentKeys)[];

/** Whether a persisted record still carries every key this build needs. False
 *  means `completeDocumentKeys` has work to do and the host must persist the
 *  result; true is the common path and costs no edit. */
export const documentKeysComplete = (
  prior?: Partial<Record<keyof DocumentKeys, unknown>>,
): boolean => prior !== undefined && DOCUMENT_KEY_NAMES.every((name) => {
  const held = Object.prototype.hasOwnProperty.call(prior, name) ? prior[name] : undefined;
  return typeof held === 'string' && held !== '';
});

/**
 * FILL IN WHATEVER A PERSISTED RECORD IS MISSING — @Codex's P1 on `d881f5b`,
 * and the defect is `0200`'s, mine.
 *
 * `version` was added to `DocumentKeys` and a host that had already persisted
 * its key record kept a record without it. `persistDocument` then called
 * `session.set(document, keys.version, …)` with `undefined`, and the store
 * refused the WHOLE edit as `unknown-slot` — so a returning installation could
 * not edit a canonical document at all. Not a degraded path: a refusal.
 *
 * `0064`'s question, asked of my own diff and not asked at the time: the
 * persisted record is a SET, this diff made it bigger, and every consumer that
 * read it back assumed it was complete.
 *
 * So the migration is not "add the version key" — that is this instance rather
 * than the shape. Anything absent is defined and REPORTED, so the next key
 * added to the table is migrated by the same call rather than by a second
 * migration nobody remembers to write. `added` is returned so a caller knows
 * whether it must persist the record again; an empty array is the common path
 * and costs no write.
 */
export function completeDocumentKeys(
  session: EditSession, prior?: Partial<Record<keyof DocumentKeys, unknown>>,
): { readonly keys: DocumentKeys; readonly added: readonly (keyof DocumentKeys)[] } {
  const keys = {} as Record<keyof DocumentKeys, PropertyKeyId>;
  const added: (keyof DocumentKeys)[] = [];
  for (const name of Object.keys(DOCUMENT_KEY_DEFS) as (keyof DocumentKeys)[]) {
    /* An own STRING is a key this host already has. `typeof` rather than a
       truthiness test, because a key id is a branded string and `''` would be
       a corrupt record rather than an absent entry — and rather than `in`,
       which answers yes for anything the prototype carries. */
    const held = prior !== undefined
      && Object.prototype.hasOwnProperty.call(prior, name) ? prior[name] : undefined;
    if (typeof held === 'string' && held !== '') {
      keys[name] = held as PropertyKeyId;
      continue;
    }
    keys[name] = session.defineProperty(DOCUMENT_KEY_DEFS[name]);
    added.push(name);
  }
  return { keys, added };
}

/** Every document key, freshly minted. The first-boot case of
 *  `completeDocumentKeys`, kept because most callers have no prior record. */
export function defineDocumentKeys(session: EditSession): DocumentKeys {
  return completeDocumentKeys(session).keys;
}

/** Create the manifests needed to read each relation without conflating sets. */
export async function putDocumentManifests(
  store: ObjectStore, authority: EditorAuthority, keys: DocumentKeys,
): Promise<DocumentManifests> {
  const values = await store.putManifest(authority, []);
  return {
    values,
    roots: await store.putManifest(authority, [{ role: 'relation.child', key: keys.roots }]),
    children: await store.putManifest(authority, [{ role: 'relation.child', key: keys.children }]),
    targets: await store.putManifest(authority, [{ role: 'relation.peer', key: keys.target }]),
  };
}

const objectId = (id: string): ObjectId => id as ObjectId;

/**
 * The bound on one document's traversals.
 *
 * Named rather than inline because the number is only half of the contract —
 * `whole` below is the other half, and a bound without it is worse than no
 * bound at all.
 */
const DOCUMENT_MAX_NODES = 100_000;

/**
 * A partial traversal is never a whole document.
 *
 * `TraversalResult.truncated` was discarded here and in `loadDocument`, so a
 * document past the bound loaded successfully and short. That is the worse
 * half of the failure: the diff would then be planned against the visible
 * prefix, and blocks the read never saw would be created a second time — or an
 * edited-and-saved document would quietly keep a tail nobody could see.
 * Measured: 12 roots read with a bound of 5 came back `truncated: 'max-nodes'`
 * and this module carried on.
 *
 * Refusing is right rather than paging: there is no correct partial answer to
 * "what does this document contain", and a store that says so is better than
 * one that guesses.
 *
 * `'depth'` is NOT one of them, and the first version of this check said it was
 * — it refused every document in the suite. A document is walked one level at a
 * time on purpose: roots from the document, then children from the roots. So
 * `'depth'` reports the bound this module asked for and got, while `'max-nodes'`
 * and `'timeout'` report an answer the store could not finish giving.
 */
function whole<T extends { readonly truncated: 'none' | 'depth' | 'max-nodes' | 'timeout' }>(result: T): T {
  if (result.truncated === 'max-nodes' || result.truncated === 'timeout')
    throw new Error(`document traversal was truncated (${result.truncated}); refusing to treat it as a whole document`);
  return result;
}

const slotValue = (object: StoredObject | undefined, key: PropertyKeyId): Value | undefined => {
  const slot = object?.slots.get(key);
  return slot?.state === 'set' ? slot.value : undefined;
};
const textAt = (object: StoredObject | undefined, key: PropertyKeyId): string | undefined => {
  const value = slotValue(object, key);
  return value?.k === 'text' ? value.v : undefined;
};
const enumAt = (object: StoredObject | undefined, key: PropertyKeyId): string | undefined => {
  const value = slotValue(object, key);
  return value?.k === 'enum' ? value.v : undefined;
};
const numberAt = (object: StoredObject | undefined, key: PropertyKeyId): number | undefined => {
  const value = slotValue(object, key);
  return value?.k === 'number' ? value.v : undefined;
};

/** One block as the store currently holds it. Its presence means the object exists. */
export interface StoredBlock {
  readonly kind?: string;
  readonly text?: string;
  readonly level?: number;
  readonly order?: number;
  /** The parent this block is linked under right now — the document, for a root. */
  readonly parent?: { readonly of: ObjectId; readonly edge?: EdgeId };
  readonly target?: { readonly of: ObjectId; readonly edge?: EdgeId };
}

/**
 * What the store already holds for one document. Every field is MEASURED.
 *
 * The version of this that took `{ documentExists?: boolean }` from the caller
 * was wrong in the way @Codex named: an existence flag the caller guesses is
 * not a substitute for having looked. It got the create right and everything
 * downstream of it wrong — a replaced root stayed linked beside its
 * replacement, a re-saved block id refused the whole commit, and an envelope
 * dropped from the content stayed on the document forever.
 */
export interface DocumentState {
  readonly exists: boolean;
  /**
   * The envelope AS STORED, not merely whether one is there.
   *
   * It was a boolean, and that made every save during a migration window write
   * it again: the plan could tell present from absent but not present from
   * unchanged. Measured — mid-migration one keystroke touched `DOCUMENT,block`
   * and an unchanged save touched `DOCUMENT`, so both of this module's own
   * cost claims were false for exactly the documents the envelope exists for.
   * The guards missed it because neither had an envelope.
   */
  readonly envelope?: string;
  /**
   * The format version AS STORED, for the reason directly above and not a
   * different one: a plan that could only tell present from absent would write
   * the version on every save, and an unchanged save must write nothing at all.
   * Absent means the document predates the slot, which is a version-1 claim
   * rather than a missing value — see `keys.version`.
   */
  readonly version?: number;
  readonly blocks: ReadonlyMap<string, StoredBlock>;
}

/**
 * Read the document graph before planning a write.
 *
 * Async, and separate from `persistDocument`, because the edit session's
 * builder is synchronous by contract (see `write.ts`) and cannot ask the store
 * anything once it has started. Takes the content it is about to persist so
 * that a block object which exists but is currently unlinked — an undo that
 * puts a deleted paragraph back — is reported as existing rather than created
 * a second time.
 */
export async function readDocumentState(
  store: ObjectReader, document: ObjectId, content: DocumentContent,
  keys: DocumentKeys, manifests: DocumentManifests,
): Promise<DocumentState> {
  const documentObject = await store.get(document, { manifest: manifests.values });
  const step = async (manifest: ManifestRef, from: readonly ObjectId[], via: 'relation.child' | 'relation.peer') =>
    from.length === 0 ? [] : whole(await store.traverse({
      manifest, from, via, direction: 'out', depth: 1, maxNodes: DOCUMENT_MAX_NODES,
    })).edges;

  const rootEdges = (await step(manifests.roots, documentObject ? [document] : [], 'relation.child'))
    .filter((edge) => edge.from === document);
  const roots = rootEdges.map((edge) => edge.to);
  const childEdges = await step(manifests.children, roots, 'relation.child');
  const linked = [...new Set([...roots, ...childEdges.map((edge) => edge.to)])];
  const targetEdges = await step(manifests.targets, linked, 'relation.peer');

  const parentOf = new Map<ObjectId, ObjectId>();
  for (const edge of rootEdges) parentOf.set(edge.to, document);
  for (const edge of childEdges) parentOf.set(edge.to, edge.from);
  const targetOf = new Map(targetEdges.map((edge) => [edge.from, edge.to] as const));

  const ids = [...new Set([...linked, ...content.blocks.map((block) => objectId(block.id))])];
  const objects = await store.getMany(ids, { manifest: manifests.values });
  const blocks = new Map<string, StoredBlock>();
  for (const object of objects) {
    const kind = enumAt(object, keys.kind);
    const text = textAt(object, keys.text);
    const level = numberAt(object, keys.level);
    const order = numberAt(object, keys.order);
    const parent = parentOf.get(object.id);
    const parentEdge = textAt(object, keys.parentEdge) as EdgeId | undefined;
    const target = targetOf.get(object.id);
    const targetEdge = textAt(object, keys.targetEdge) as EdgeId | undefined;
    blocks.set(object.id, {
      ...(kind !== undefined && { kind }),
      ...(text !== undefined && { text }),
      ...(level !== undefined && { level }),
      ...(order !== undefined && { order }),
      ...(parent !== undefined && { parent: { of: parent, ...(parentEdge !== undefined && { edge: parentEdge }) } }),
      ...(target !== undefined && { target: { of: target, ...(targetEdge !== undefined && { edge: targetEdge }) } }),
    });
  }
  const envelope = textAt(documentObject, keys.legacy);
  const storedVersion = documentObject === undefined ? undefined : numberAt(documentObject, keys.version);
  return { exists: documentObject !== undefined, ...(envelope !== undefined && { envelope }),
    ...(storedVersion !== undefined && { version: storedVersion }), blocks };
}

type BlockValue =
  | { readonly k: 'enum'; readonly v: string }
  | { readonly k: 'text'; readonly v: string }
  | { readonly k: 'number'; readonly v: number };

const edgeOrThrow = (block: string, edge: EdgeId | undefined, what: string): EdgeId => {
  if (edge !== undefined) return edge;
  // Unreachable for a graph this module wrote, and worth saying out loud rather
  // than leaving the stale edge in place: a root that will not come off is a
  // paragraph somebody deleted that reappears on every reload.
  throw new Error(`document block ${block} has an unrecorded ${what} edge, which cannot be unlinked`);
};

/**
 * Persist one validated document as addressable objects plus normalized edges.
 * Root and child order are values on the child object; the edges express
 * membership and embed targets remain relations to their canonical objects.
 *
 * A DIFF, not a rewrite. Every step compares the content against `state` and
 * emits only what actually differs, for two reasons that pull the same way.
 * Correctness: `session.link` is additive — the editor door mints a fresh edge
 * per call and traversal merely deduplicates by endpoint, so re-linking an
 * unchanged root accumulates invisible duplicates. And cost: decision 0025 asks
 * that *"the document visibly saves as it is written"*, so a keystroke in one
 * paragraph has to commit one `set`, not a re-creation of the whole tree.
 *
 * Blocks removed from the content are unlinked and left addressable rather than
 * retired. Something else may still point at one — a caret anchor, an inbound
 * citation — and a tombstone would break those to tidy up a graph that
 * `loadDocument` already cannot see.
 */
/**
 * What the same commit must also take away.
 *
 * Decision 0028: *"the first successful canonical document commit establishes
 * the blocks and retires the stored scalar in the same logical migration. A
 * partially committed state must not make both representations writable."*
 *
 * The key is the CALLER's to name, because the scalar it retires is pool
 * vocabulary — `body` on a note — and this file owns document vocabulary. Point
 * 4 of the ruling on task 0100 is the same boundary from the other side: block
 * internals stay under private keys rather than borrowing public roles, and by
 * the same token the document writer does not get to reach into a pool's.
 *
 * SAME COMMIT, not a second one afterwards. Two commits have a window between
 * them in which both representations are writable, and a failure inside that
 * window leaves a note with blocks AND a live scalar — which is precisely the
 * state task 0105 exists to remove.
 */
export interface DocumentRetirement {
  /** The scalar to clear on the document object, in this same commit. */
  readonly scalar: PropertyKeyId;
}

export function persistDocument(
  session: EditSession, document: ObjectId, content: DocumentContent,
  keys: DocumentKeys, state: DocumentState, retire?: DocumentRetirement,
): void {
  const problems = validateDocumentContent(content);
  if (problems.length) throw new Error(`invalid document content: ${problems[0]!.path} ${problems[0]!.message}`);
  if (!state.exists) session.create({ id: document, typeHint: 'document' });
  // The ownership transfer, in the same batch as the blocks that take over.
  if (retire) session.clear(document, retire.scalar);
  /**
   * The migration envelope travels WITH the document, not beside it.
   *
   * `loadDocument` rebuilt content from objects and edges and dropped
   * `legacySeparators`, so a note migrated from `alpha\r\n\r\nbeta` came back
   * out of `blocksToLegacyBody` as `alpha\n\nbeta`. Measured, and it is a
   * silent rewrite of somebody's own writing — the exact loss the byte-for-byte
   * round trip was built to prevent, reintroduced one layer down.
   *
   * It is also the one field an ordinary edit REMOVES: the compatibility writer
   * drops it once the legacy Body no longer has to round-trip. A save that
   * omits it has to clear it, or the document keeps claiming delimiters its
   * roots no longer have and the validator rejects it on the next load.
   */
  const envelope = content.legacySeparators !== undefined
    ? JSON.stringify(content.legacySeparators) : undefined;
  if (envelope !== state.envelope) {
    if (envelope === undefined) session.clear(document, keys.legacy);
    else session.set(document, keys.legacy, { k: 'text', v: envelope });
  }

  /* THE VERSION, DERIVED FROM THE CONTENT AND WRITTEN UNCONDITIONALLY —
     decision `0075` rulings 1 and 6.

     Not the version the content was READ at: a save is the genuine edit
     migration is lazy on, so a grandfathered version-1 envelope carrying a
     quote goes back out as 2, because 2 is the lowest version admitting a
     quote. And not `CURRENT_DOCUMENT_VERSION` either — a document of nothing
     but paragraphs stays 1, because a version-1 reader can still read,
     preserve, edit and write it back, and inflating it would refuse it to a
     reader perfectly capable of it.

     OUTSIDE the legacy-envelope branch, and the first version of this was
     inside it. That branch runs only when the migration envelope CHANGES,
     which for an ordinary document is never — so the version was written only
     for documents carrying legacy separators, and the grandfathering control
     read 1 where it required 2.

     AND WRITTEN ONLY WHEN IT DIFFERS, which the second version of this got
     wrong in the other direction: unconditional writes made an unchanged save
     touch the document object, and `writes nothing at all when nothing
     changed` is a guarantee this module measures itself against. That is the
     same lesson `state.envelope` already carries one field up — present from
     absent is not present from unchanged — arriving in the field added next
     to it. */
  /* CURRENT, whatever the content — @Codex's P1 on `d881f5b`, and decision
     `0075` had already ruled it in the section reviewing my reconstruction.
     This read `requiredDocumentVersion(content.blocks)`, so a current writer
     editing a paragraph-only document stamped 1, and removing the last quote
     from a v2 document took it BACK to 1. That makes the envelope a per-kind
     feature flag; it is the writer/reader contract, and version 1 is reserved
     for what an unchanged legacy document is read at.

     Still written only when it DIFFERS, which is unchanged and is what makes
     ruling 6's lazy upgrade fall out: a grandfathered v1 envelope is read at
     1, `state.version` is 1, and the first genuine edit writes 2 — while a
     document already at 2 writes nothing, so `writes nothing at all when
     nothing changed` still holds. */
  if (CURRENT_DOCUMENT_VERSION !== state.version) {
    session.set(document, keys.version, { k: 'number', v: CURRENT_DOCUMENT_VERSION });
  }

  // Values. `typeHint` says `document-block` and no more: kind lives in a slot
  // because a paragraph becomes a heading, and there is no API to correct a
  // type hint afterwards — one that encoded the kind would be a permanent lie.
  for (const block of content.blocks) {
    const id = objectId(block.id);
    const was = state.blocks.get(block.id);
    if (was === undefined) {
      const properties: [PropertyKeyId, BlockValue][] = [[keys.kind, { k: 'enum', v: block.kind }]];
      if (block.text !== undefined) properties.push([keys.text, { k: 'text', v: block.text }]);
      if (block.level !== undefined) properties.push([keys.level, { k: 'number', v: block.level }]);
      session.create({ id, typeHint: 'document-block', properties });
      continue;
    }
    if (was.kind !== block.kind) session.set(id, keys.kind, { k: 'enum', v: block.kind });
    // CLEARED, not left behind. A heading demoted to a paragraph keeps its
    // level otherwise, and `validateDocumentContent` refuses to load it back:
    // "level is only valid on headings".
    if (was.text !== block.text) {
      if (block.text === undefined) session.clear(id, keys.text);
      else session.set(id, keys.text, { k: 'text', v: block.text });
    }
    if (was.level !== block.level) {
      if (block.level === undefined) session.clear(id, keys.level);
      else session.set(id, keys.level, { k: 'number', v: block.level });
    }
  }

  // Structural parents. Roots and list items are one relation with two keys, so
  // a paragraph promoted out of a list is a single move rather than a delete
  // and a create that would lose the object's identity.
  const parentOf = new Map<string, ObjectId>();
  const orderOf = new Map<string, number>();
  content.roots.forEach((id, order) => { parentOf.set(id, document); orderOf.set(id, order); });
  for (const block of content.blocks)
    block.children?.forEach((id, order) => { parentOf.set(id, objectId(block.id)); orderOf.set(id, order); });

  for (const [id, was] of state.blocks) {
    if (was.parent === undefined || parentOf.get(id) === was.parent.of) continue;
    session.unlink(edgeOrThrow(id, was.parent.edge, 'containment'));
    if (!parentOf.has(id)) session.clear(objectId(id), keys.parentEdge);
  }
  for (const [id, parent] of parentOf) {
    if (state.blocks.get(id)?.parent?.of === parent) continue;
    const edge = session.link(parent, parent === document ? keys.roots : keys.children, objectId(id));
    session.set(objectId(id), keys.parentEdge, { k: 'text', v: edge });
  }
  for (const [id, order] of orderOf)
    if (state.blocks.get(id)?.order !== order) session.set(objectId(id), keys.order, { k: 'number', v: order });

  // Embed targets.
  const targetOf = new Map<string, ObjectId>();
  for (const block of content.blocks)
    if (block.target !== undefined) targetOf.set(block.id, objectId(block.target));
  for (const [id, was] of state.blocks) {
    if (was.target === undefined || targetOf.get(id) === was.target.of) continue;
    session.unlink(edgeOrThrow(id, was.target.edge, 'embed'));
    if (!targetOf.has(id)) session.clear(objectId(id), keys.targetEdge);
  }
  for (const [id, target] of targetOf) {
    if (state.blocks.get(id)?.target?.of === target) continue;
    const edge = session.link(objectId(id), keys.target, target);
    session.set(objectId(id), keys.targetEdge, { k: 'text', v: edge });
  }
}

/** One in-flight save per document, per store. See `saveDocument`. */
const saveQueues = new WeakMap<ObjectStore, Map<ObjectId, Promise<unknown>>>();

/**
 * Read, then plan, then commit — the whole save in one call, one at a time.
 *
 * The two halves are exported separately for a caller that has other work to
 * put in the same commit, but this is the shape every ordinary save wants, and
 * it is the shape that cannot be got wrong by forgetting to read first.
 *
 * **This used to claim the read/write window was closed by optimistic
 * concurrency, and that was simply false.** The BINDING door checks revisions —
 * `apply` compares `g.revision` and reports `conflicts` — and the EDITOR door
 * takes no revision and checks none. So two saves could read one graph and both
 * commit. Measured, with the item moved to a different list by each:
 *
 * ```
 * two overlapping saves -> first ok: true | second ok: true | in B: 1 | in C: 1
 * the document then loads -> "block ... has more than one structural parent"
 * ```
 *
 * Not a lost update: an UNLOADABLE document. The second save's unlink was a
 * no-op against an edge the first had already removed, so both parents
 * survived and the tree the whole vocabulary rests on stopped being a tree.
 *
 * What the queue closes is contention within one store: saves for one document
 * run one after another, so the second reads what the first committed and plans
 * a move rather than a duplicate.
 *
 * **What it never closed is correctness**, and decision 0027 says so by name:
 * the queue *"may stay as a contention optimization, but it must pass the
 * snapshot from which `loadDocument` derived the save plan. The queue alone is
 * not correctness."* It is per store instance; two tabs on one IndexedDB hold
 * two engines and interleave straight past it.
 *
 * So the plan is now built inside `read`, which validates every await of
 * `readDocumentState` against one revision, and the commit names that same
 * revision. The read and the write describe one state or the write is refused.
 *
 * The commit is deliberately OUTSIDE the read: `read` retries, and a retry
 * would replay a commit that had already landed. Plan inside, write outside.
 */
export async function saveDocument(
  store: ObjectStore, authority: EditorAuthority, document: ObjectId,
  content: DocumentContent, keys: DocumentKeys, manifests: DocumentManifests,
  retire?: DocumentRetirement,
  /**
   * THE CALLER'S COMMIT BASIS, when the caller has predicates of its own —
   * task `0219`, from @Codex's P1.
   *
   * Without it this function acquires its own snapshot AND its own basis, so a
   * caller that decided whether to write by reading first decided on one state
   * and committed against another. Two tabs opening an older database is the
   * case they named: one observes that a note has no canonical document, the
   * other authors one, and the first then writes anyway — replacing somebody's
   * roots and text with the stock seed.
   *
   * No care at the call site can close that while the basis is minted in here,
   * which is why this is a parameter and not a guard. It is the shape `0105`
   * already rules for the read side, quoted directly below: *the caller must
   * be able to retain the complete ReadSnapshot … then pass that same snapshot
   * to any resulting edit.* `loadDocument` takes the caller's reader; this
   * takes the revision that reader was at, which `ReadSnapshot` documents as
   * "what a write built from this snapshot must name as its commit basis".
   *
   * A stale one is REFUSED rather than applied — `applyCommit` commits with
   * `expect: basis` and returns `stale` with no part of the batch persisted —
   * and for a conditional seed a refusal is the right answer, because the
   * thing its predicate was about is no longer true.
   *
   * `state` is still read here, on a newer snapshot, and that is sound: if the
   * world moved the commit is refused and this state never reaches the
   * backend; if it did not, the basis is current and the two describe one
   * state.
   */
  basis?: number,
): Promise<CommitResult> {
  let queues = saveQueues.get(store);
  if (!queues) saveQueues.set(store, queues = new Map());
  const run = (queues.get(document) ?? Promise.resolve()).then(async () => {
    const { state, basis: own } = await store.read(async ({ reader, revision }) => ({
      state: await readDocumentState(reader, document, content, keys, manifests),
      basis: revision,
    }));
    return store.edit(authority, basis ?? own,
      (session) => persistDocument(session, document, content, keys, state, retire));
  });
  // The QUEUE entry must never reject, or one refused save would poison every
  // later one for the same document. The caller still sees the real outcome.
  queues.set(document, run.catch(() => undefined));
  return run;
}

/**
 * Rehydrate semantics from objects and relation sets, never serialized markup.
 *
 * TAKES A READER, AND THE CALLER SUPPLIES IT. @Codex, ruling the shape for task
 * 0105 phase 1:
 *
 * > *"the caller must be able to retain the complete `ReadSnapshot`, pass
 * > `snapshot.reader` into document traversal and related projection reads,
 * > then pass that same snapshot to any resulting edit."*
 *
 * Two things need that and neither can get it from a loader that acquires its
 * own snapshot invisibly. Decision 0028's derived `body` has to compose inside
 * a LARGER coherent read — establishing that blocks exist, loading them, and
 * resolving embed labels all share one reader. And retiring the leftover legacy
 * scalar has to be conditioned on the revision paired with the reader that
 * established the facts, or the cleanup has no honest basis.
 *
 * Every read below goes through this one reader. A convenience wrapper that
 * takes a store may be added beside this, never instead of it.
 */
export async function loadDocument(
  store: ObjectReader, document: ObjectId, keys: DocumentKeys, manifests: DocumentManifests,
): Promise<DocumentContent | undefined> {
  const documentObject = await store.get(document, { manifest: manifests.values });
  if (!documentObject) return undefined;
  const traverse = async (manifest: ManifestRef, from: readonly ObjectId[], via: 'relation.child' | 'relation.peer') =>
    whole(await store.traverse({ manifest, from, via, direction: 'out', depth: 1, maxNodes: DOCUMENT_MAX_NODES }));
  const rootGraph = await traverse(manifests.roots, [document], 'relation.child');
  const rootIds = rootGraph.edges.filter((edge) => edge.from === document).map((edge) => edge.to);
  /**
   * NO ROOTS MEANS NOT A DOCUMENT, and that is unambiguous rather than a guess.
   *
   * `validateDocumentContent` refuses content with no blocks, so `persistDocument`
   * cannot create a rootless document; a stored object with no root edges has
   * never been written as one. Every pool object exists — a note is an object —
   * so without this the loader read "the object is there, it has no roots" as a
   * CORRUPT document and threw.
   *
   * It did, on the ordinary URL, the moment the canvas started asking every note
   * whether it was a document yet: `persisted document is invalid: roots a
   * document has at least one block`, thrown for eight perfectly ordinary notes
   * that simply had not been migrated. The caller's question is "is there a
   * document here", and `undefined` is the answer to it.
   */
  if (rootIds.length === 0) return undefined;
  const rootObjects = await store.getMany(rootIds, { manifest: manifests.values });
  const rootOrder = new Map(rootObjects.map((o) => [o.id, numberAt(o, keys.order) ?? 0]));
  const roots = [...rootIds].sort((a, b) => rootOrder.get(a)! - rootOrder.get(b)!);

  const childGraph = roots.length
    ? await traverse(manifests.children, roots, 'relation.child')
    : { edges: [] as readonly { readonly from: ObjectId; readonly to: ObjectId }[] };
  const childIds = childGraph.edges.map((edge) => edge.to);
  const allIds = [...new Set([...roots, ...childIds])];
  const objects = await store.getMany(allIds, { manifest: manifests.values });
  const byId = new Map(objects.map((o) => [o.id, o]));
  const childrenByParent = new Map<ObjectId, ObjectId[]>();
  for (const edge of childGraph.edges) {
    const children = childrenByParent.get(edge.from) ?? [];
    children.push(edge.to);
    childrenByParent.set(edge.from, children);
  }
  for (const children of childrenByParent.values()) children.sort((a, b) =>
    (numberAt(byId.get(a), keys.order) ?? 0) - (numberAt(byId.get(b), keys.order) ?? 0));

  const targetGraph = allIds.length
    ? await traverse(manifests.targets, allIds, 'relation.peer')
    : { edges: [] as readonly { readonly from: ObjectId; readonly to: ObjectId }[] };
  const targetByBlock = new Map(targetGraph.edges.map((edge) => [edge.from, edge.to]));
  /**
   * Roots in order, then each root's children in order — a CANONICAL block
   * order rather than whatever order the traversal happened to return edges in.
   * `blocks` is an array, so two loads of one document that disagree about its
   * order are two different documents to anything comparing them, and the
   * comparison is not hypothetical: it is how a save decides it has nothing to
   * write.
   */
  const orderedIds = [...new Set([...roots, ...roots.flatMap((root) => childrenByParent.get(root) ?? [])])];
  const blocks: DocumentBlock[] = orderedIds.map((id) => {
    const object = byId.get(id);
    const kind = enumAt(object, keys.kind) as DocumentBlock['kind'] | undefined;
    if (!kind) throw new Error(`document block ${id} has no kind`);
    const text = textAt(object, keys.text);
    const level = numberAt(object, keys.level) as DocumentBlock['level'] | undefined;
    // A LIST IS A LIST WHETHER OR NOT IT HAS ITEMS. An empty list writes no
    // child edge, so this returned undefined, the block came back without
    // `children`, and the validator below rejected a document that had just
    // saved successfully. Measured: "blocks[0].children is required only on
    // list blocks" on reload. An empty list is the ordinary state for the
    // second after somebody inserts one. @Codex's P1.
    const children = kind === 'list'
      ? (childrenByParent.get(id) ?? []).map(String)
      : childrenByParent.get(id)?.map(String);
    const target = targetByBlock.get(id);
    return { id, kind, ...(text !== undefined && { text }), ...(level !== undefined && { level }),
      ...(children !== undefined && { children }), ...(target !== undefined && { target: String(target) }) };
  });
  const envelope = textAt(documentObject, keys.legacy);
  /* ABSENT MEANS 1 — see the key's own comment. Every document persisted
     before this slot existed was written by a build whose only version was 1,
     so the absence is that build's claim rather than a missing value, and the
     spec's grandfathering rule reads it even when it carries a v2 kind. */
  const stored = numberAt(documentObject, keys.version);
  const version = (stored === undefined ? 1 : stored) as DocumentContent['version'];
  const content: DocumentContent = {
    version, roots: roots.map(String), blocks,
    ...(envelope !== undefined ? { legacySeparators: JSON.parse(envelope) as string[] } : {}),
  };
  const problems = validateDocumentContent(content);
  if (problems.length) throw new Error(`persisted document is invalid: ${problems[0]!.path} ${problems[0]!.message}`);
  return content;
}
